import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { localizeWithFallback } from '../utils/localization-utils';
import { ZnSelectQueryParams } from '../generic-form/interfaces/IGenericFieldOptions';
import { ZnSelectLoader, ZnSelectOptionsLoader } from './zn-select-loader';
import type { ZnSelectOption } from './zn-select.types';
import {
  buildZnSelectLoadOptionsErrorMessage,
  createZnSelectOverlayConfig,
  normalizeZnSelectIds,
  resolveSinglePlaceholder,
} from './zn-select-helpers';
import { ZnButton } from '../zn-button/zn-button';

@Component({
  selector: 'zn-single-select',
  standalone: true,
  imports: [CommonModule, FormsModule, ZendraLocalizationPipe, OverlayModule, ZnButton],
  templateUrl: './zn-single-select.html',
  styleUrls: ['./zn-select.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ZnSingleSelect),
      multi: true,
    },
  ],
})
export class ZnSingleSelect implements ControlValueAccessor, OnDestroy {
  private static masterCheckSequence = 0;

  readonly masterCheckId = `zn-master-check-${ZnSingleSelect.masterCheckSequence++}`;

  options = input<Array<string | { key: string; value: string }>>([]);
  optionsLoader = input<ZnSelectOptionsLoader | undefined>(undefined);
  showSearch = input<boolean>(true);
  placeholder = input<string>('');
  showPlaceholder = input<boolean>(true);
  showClearSelection = input<boolean>(true);
  errorEntityKey = input<string>('');
  resolveIds = input<boolean>(true);
  localizationResource = input<ZendraLocalizationServices>(
    ZendraLocalizationServices.LanguageService,
  );

  @Input() openUp = false;

  selectedChange = output<string | string[] | null>();
  tabNavigation = output<boolean>();
  tabNavigationEnabled = input<boolean>(false);

  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<any>;
  @ViewChild('selectTrigger', { read: ElementRef })
  selectTrigger?: ElementRef<HTMLElement>;
  @ViewChild('searchInput', { read: ElementRef })
  searchInput?: ElementRef<HTMLInputElement>;

  readonly dropdownOpen = signal(false);
  readonly search = signal('');
  readonly selected = signal<string[]>([]);
  readonly activeIndex = signal<number>(-1);
  readonly loading = signal<boolean>(false);
  readonly loadingMore = signal<boolean>(false);
  readonly focused = signal(false);

  private readonly asyncOptions = signal<ZnSelectOption[]>([]);
  private readonly staticOptions = signal<ZnSelectOption[]>([]);
  private readonly currentValue = signal<string[] | string | null>(null);
  private readonly loaderTick = signal(0);
  private readonly totalCount = signal<number | null>(null);
  private readonly lastBatchSize = signal<number>(0);
  private readonly keyPressed = signal(false);

  readonly displayOptions = computed(() => {
    return this.optionsLoader() ? this.asyncOptions() : this.staticOptions();
  });

  readonly inputValue = computed(() => {
    if (this.focused()) {
      return this.search();
    }

    const selected = this.selected();

    if (!selected.length) {
      return '';
    }

    const rawLabel = this.getLabelForId(selected[0]);
    if (!rawLabel) return '';

    return this.localizeValue(rawLabel);
  });

  readonly filteredOptions = computed(() => {
    const options = this.displayOptions();
    if (this.optionsLoader()) {
      return options;
    }

    const term = this.search().trim().toLowerCase();
    if (!this.showSearch() || !term) {
      return options;
    }

    return options.filter(option => option.value.toLowerCase().includes(term));
  });

  readonly selectedLabel = computed(() => {
    this.loaderTick();
    const selected = this.selected();
    if (!selected.length) {
      return '';
    }

    const id = selected[0];
    const rawLabel = this.selectedValueMap.get(id) ?? this.optionsMap.get(id) ?? id;

    return this.localizeValue(rawLabel);
  });

  readonly displayPlaceholder = computed(() =>
    resolveSinglePlaceholder(
      this.placeholder(),
      this.showPlaceholder(),
      this.selectedLabel(),
    ),
  );

  readonly inputPlaceholderValue = computed(() => {
    const placeholder = this.displayPlaceholder();
    if (!placeholder) {
      return '';
    }

    return this.localizeValue(placeholder);
  });

  readonly selectedLabels = computed(() => {
    this.loaderTick();
    return this.selected().map(id => {
      const rawLabel = this.getLabelForId(id);
      return rawLabel ? this.localizeValue(rawLabel) : rawLabel;
    });
  });

  readonly hasMore = computed(() => this.loader.hasMore());
  readonly hasSearchValue = computed(() => this.search().trim().length > 0);
  readonly hasSelectedValue = computed(() => this.selected().length > 0);

  readonly rightIcon = computed(() => {
    if (this.hasSearchValue() || this.hasSelectedValue()) {
      return 'clear';
    }

    if (this.focused() || this.dropdownOpen()) {
      return 'refresh';
    }

    return 'chevron';
  });

  disabled = false;

  private readonly localizationService = inject(LocalizationService);
  private readonly toaster = inject(ToasterService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private readonly optionsMap = new Map<string, string>();
  private readonly selectedValueMap = new Map<string, string>();
  private readonly pendingResolveIds = new Set<string>();

  private readonly pageSize = 20;
  private skipCount = 0;
  private overlayRef: OverlayRef | null = null;

  private onChange: (value: string | string[] | null) => void = () => {};
  private onTouched: () => void = () => {};
  private labelLoadSubscriptions: Subscription[] = [];

  private readonly loader: ZnSelectLoader;

  constructor() {
    this.loader = new ZnSelectLoader({
      getLoader: () => this.optionsLoader(),
      getPageSize: () => this.pageSize,
      getSkipCount: () => this.skipCount,
      setSkipCount: value => {
        this.skipCount = value;
      },
      getSearch: () => this.search(),
      isDropdownOpen: () => this.dropdownOpen(),
      getOptionsContainer: () => this.getOptionsContainerElement(),
      setLoading: value => this.loading.set(value),
      setLoadingMore: value => this.loadingMore.set(value),
      onOptionsLoaded: (payload, total, append, previousScrollTop) => {
        if (!append) {
          this.optionsMap.clear();

          // mantener labels ya resueltos
          this.selected().forEach(id => {
            const label = this.selectedValueMap.get(id);

            if (label) {
              this.optionsMap.set(id, label);
            }
          });

          this.asyncOptions.set([]);
        }
        payload.forEach(option => {
          const key = String(option.key);
          const value = String(option.value);
          this.optionsMap.set(key, value);
          this.selectedValueMap.set(key, value);
        });

        const nextOptions: ZnSelectOption[] = payload.map(option => ({
          key: String(option.key),
          value: String(option.value),
        }));

        if (append) {
          this.asyncOptions.update(current => [...current, ...nextOptions]);
        } else {
          this.asyncOptions.set(nextOptions);
        }

        if (total !== null) {
          this.totalCount.set(total);
        }

        this.loaderTick.update(value => value + 1);

        const optionsContainer = this.getOptionsContainerElement();
        if (append && optionsContainer) {
          requestAnimationFrame(() => {
            optionsContainer.scrollTo({ top: previousScrollTop });
          });
        }
      },
      onLoadError: error => {
        this.toaster.error(this.buildLoadOptionsErrorMessage(error), null, {
          life: 5000,
        });
      },
      getAsyncOptionsLength: () => this.asyncOptions().length,
      getTotalCount: () => this.totalCount(),
      setTotalCount: value => this.totalCount.set(value),
      setLastBatchSize: value => this.lastBatchSize.set(value),
      getLastBatchSize: () => this.lastBatchSize(),
      onResetOptions: () => {
        this.asyncOptions.set([]);
        this.optionsMap.clear();
      },
    });

    effect(() => {
      const rawOptions = this.options();
      const hasLoader = !!this.optionsLoader();

      if (!hasLoader) {
        this.optionsMap.clear();
        this.selectedValueMap.clear();

        this.staticOptions.set(
          rawOptions.map(option => {
            if (typeof option === 'string') {
              return { key: option, value: option };
            }

            return {
              key: String(option.key),
              value: String(option.value),
            };
          }),
        );
      }

      rawOptions.forEach(option => {
        if (typeof option === 'string') {
          this.optionsMap.set(option, option);
          this.selectedValueMap.set(option, option);
          return;
        }

        const key = String(option.key);
        const value = String(option.value);
        this.optionsMap.set(key, value);
        this.selectedValueMap.set(key, value);
      });

      const current = this.currentValue();
      if (current !== null && current !== undefined) {
        this.syncFromValue(current);
      }
    });

    effect(() => {
      const loader = this.optionsLoader();
      this.loaderTick();

      const value = this.currentValue();

      if (!loader || !value) {
        return;
      }

      const ids = normalizeZnSelectIds(value);

      const hasMissingLabels = ids.some(id => !this.selectedValueMap.has(id));

      if (hasMissingLabels) {
        this.resolveSelectedLabels(value);
      }
    });

    effect(() => {
      if (!this.dropdownOpen()) {
        return;
      }

      const options = this.filteredOptions();
      if (options.length === 0) {
        this.activeIndex.set(-1);
        return;
      }

      if (this.activeIndex() < 0 || this.activeIndex() >= options.length) {
        this.activeIndex.set(0);
      }
    });
  }

  writeValue(value: string[] | string | null): void {
    this.currentValue.set(value);
    this.syncFromValue(value);

    const normalized = normalizeZnSelectIds(value);
    const hasMissingLabels = normalized.some(id => !this.selectedValueMap.has(id));
    if (hasMissingLabels) {
      this.resolveSelectedLabels(value);
    }
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  clearSearch(): void {
    this.search.set('');

    if (this.optionsLoader()) {
      this.loader.resetPagination();
      this.loadOptions({ forceRefresh: true, search: '' });
    }
  }

  toggleDropdown(): void {
    if (this.disabled) {
      return;
    }

    if (this.dropdownOpen()) {
      this.closeDropdown();
      return;
    }

    this.openDropdown();
  }

  open(): void {
    if (this.disabled || this.dropdownOpen()) {
      return;
    }

    this.openDropdown();
    this.focusSearch();
  }

  isSelected(id: string | number): boolean {
    return this.selected().includes(String(id));
  }

  toggle(id: string | number): void {
    if (this.disabled) {
      return;
    }

    const value = String(id);

    this.selected.set([value]);
    this.currentValue.set(value);

    this.emitSelection([value]);
    this.closeDropdown();
  }

  onOptionClick(event: MouseEvent, id: string | number): void {
    event.stopPropagation();
    this.toggle(id);
  }

  clearSelection(): void {
    if (this.disabled) {
      return;
    }

    this.selected.set([]);
    this.currentValue.set(null);
    this.emitSelection([]);
    this.search.set('');
    this.closeDropdown();
  }

  refreshOptions(): void {
    if (this.disabled || this.loading() || !this.optionsLoader()) {
      return;
    }
    this.search.set('');

    this.loader.resetPagination();
    this.loadOptions({ forceRefresh: true, search: this.search() });
  }

  handleKeydown(event: KeyboardEvent): void {
    this.keyPressed.set(true);
    const key = event.key;

    if (key === 'Tab') {
      if (this.tabNavigationEnabled()) {
        event.preventDefault();
        event.stopPropagation();
        this.closeDropdown();
        this.tabNavigation.emit(event.shiftKey);
        return;
      }

      this.closeDropdown();
    }

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      if (this.dropdownOpen()) {
        this.closeDropdown();
      }
      return;
    }

    if (!this.dropdownOpen()) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        this.openDropdown();
        this.focusSearch();
      }
      return;
    }

    const options = this.filteredOptions();

    if (key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (options.length === 0) {
        return;
      }

      const current = this.activeIndex();
      const next = current < 0 ? 0 : Math.min(current + 1, options.length - 1);
      this.activeIndex.set(next);
      this.scrollActiveOptionIntoView(true);
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (options.length === 0) {
        return;
      }

      const current = this.activeIndex();
      const next = current < 0 ? options.length - 1 : Math.max(current - 1, 0);
      this.activeIndex.set(next);
      this.scrollActiveOptionIntoView(true);
      return;
    }

    if (key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      const index = this.activeIndex();
      if (index >= 0 && index < options.length) {
        this.toggle(options[index].key);
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    }
  }

  onSearchChange(value: string): void {
    this.search.set(value);

    if (!this.optionsLoader()) {
      return;
    }

    this.loader.queueSearch(value);
  }

  onFocus(): void {
    this.focused.set(true);
  }

  onBlur(): void {
    this.focused.set(false);
  }

  onRightIconClick(event: MouseEvent): void {
    event.stopPropagation();

    const icon = this.rightIcon();

    if (icon === 'clear') {
      if (this.hasSearchValue()) {
        this.clearSearch();
        return;
      }

      this.clearSelection();
      return;
    }

    if (icon === 'refresh') {
      this.refreshOptions();
      return;
    }

    this.toggleDropdown();
  }

  onOptionsScroll(event: Event): void {
    if (this.loading() || this.loadingMore() || !this.optionsLoader()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const threshold = 24;
    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
    if (!nearBottom || !this.hasMore()) {
      return;
    }

    this.loader.loadNextPage();
  }

  isActiveIndex(index: number): boolean {
    if (this.keyPressed()) {
      return index === this.activeIndex();
    }
  }

  getLabelForId(id: string): string {
    return this.selectedValueMap.get(id) ?? this.optionsMap.get(id) ?? '';
  }

  toLocalizationKey(value: string): string {
    if (!value) {
      return '';
    }

    return value.includes('::') ? value : `${this.localizationResource()}::${value}`;
  }

  ngOnDestroy(): void {
    this.loader.destroy();
    this.labelLoadSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.labelLoadSubscriptions = [];

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private syncFromValue(value: string[] | string | null): void {
    this.selected.set(normalizeZnSelectIds(value).slice(0, 1));
  }

  private emitSelection(values: string[]): void {
    const singleValue = values.length > 0 ? values[0] : null;
    this.onChange(singleValue);
    this.selectedChange.emit(singleValue);
  }

  private openDropdown(): void {
    this.dropdownOpen.set(true);
    this.activeIndex.set(-1);

    if (this.optionsLoader()) {
      this.loader.resetPagination();

      const params: ZnSelectQueryParams = {
        search: '',
      };

      const selectedIds = this.selected();
      if (selectedIds.length > 0) {
        params.ids = selectedIds;
      }

      this.loadOptions(params);
    }

    this.scrollActiveOptionIntoView(false);

    if (!this.selectTrigger?.nativeElement) {
      return;
    }

    const config = createZnSelectOverlayConfig(
      this.overlay,
      this.selectTrigger.nativeElement,
      this.openUp,
    );
    this.overlayRef = this.overlay.create(config);

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      const target = event.target as HTMLElement;

      const clickedInsideTrigger = this.selectTrigger?.nativeElement.contains(target);

      if (!clickedInsideTrigger) {
        this.closeDropdown();
      }
    });
  }

  private closeDropdown(): void {
    this.dropdownOpen.set(false);
    this.onTouched();
    this.activeIndex.set(-1);

    this.search.set('');

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    this.keyPressed.set(false);
  }

  private getOptionsContainerElement(): HTMLElement | undefined {
    return (
      this.overlayRef?.overlayElement.querySelector<HTMLElement>(
        '.zn-dropdown-menu-body',
      ) ?? undefined
    );
  }

  private loadOptions(params?: ZnSelectQueryParams, append: boolean = false): void {
    this.loader.loadOptions(params, append);
  }

  private focusSearch(): void {
    requestAnimationFrame(() => {
      this.searchInput?.nativeElement?.focus();
    });
  }

  private scrollActiveOptionIntoView(smooth: boolean): void {
    const container = this.getOptionsContainerElement();
    if (!container) {
      return;
    }

    const items = container.querySelectorAll<HTMLElement>('.option-item');
    const index = this.activeIndex();
    if (index < 0 || index >= items.length) {
      return;
    }

    const item = items[index];
    requestAnimationFrame(() => {
      item.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: smooth ? 'smooth' : 'auto',
      });
    });
  }

  private resolveSelectedLabels(value: string[] | string | null): void {
    if (!this.resolveIds()) {
      return;
    }

    const loader = this.optionsLoader();
    if (!loader || !value) {
      return;
    }

    const ids = Array.isArray(value) ? value : [value];
    ids.forEach(id => {
      const idStr = String(id);
      if (this.selectedValueMap.has(idStr) || this.pendingResolveIds.has(idStr)) {
        return;
      }

      const directOption = this.options().find(
        option => typeof option !== 'string' && String(option.key) === idStr,
      ) as { key: string; value: string } | undefined;

      if (directOption) {
        const label = String(directOption.value);
        this.optionsMap.set(idStr, label);
        this.selectedValueMap.set(idStr, label);
        return;
      }

      this.pendingResolveIds.add(idStr);
      const subscription = loader({
        ids: [idStr],
        forceRefresh: false,
        preferCache: true,
      }).subscribe({
        next: response => {
          const payload = Array.isArray(response) ? response : response.items;
          this.pendingResolveIds.delete(idStr);
          if (!payload?.length) {
            return;
          }

          const match =
            payload.find(item => String(item?.key) === idStr) ??
            (payload.length === 1 ? payload[0] : null);
          if (!match) {
            return;
          }

          const key = String(match.key);
          const label = String(match.value);
          this.optionsMap.set(key, label);
          this.selectedValueMap.set(key, label);
          this.syncFromValue(this.currentValue());
        },
        error: error => {
          this.toaster.error(this.buildLoadOptionsErrorMessage(error), null, {
            life: 5000,
          });
          this.pendingResolveIds.delete(idStr);
        },
      });

      this.labelLoadSubscriptions.push(subscription);
    });
  }

  private buildLoadOptionsErrorMessage(error?: unknown): string {
    return buildZnSelectLoadOptionsErrorMessage(
      this.localizationService,
      this.localizationResource(),
      this.errorEntityKey(),
      error,
    );
  }

  private localizeValue(value: string): string {
    return localizeWithFallback(this.localizationService, this.toLocalizationKey(value));
  }
}
